import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import axios from "axios";
import { operationType, selectDataType } from "../../../../../services/types";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { ExpandedBlock } from "../../../expanded";
import { AuthenticateSelect } from "../../../../auth/select";
import { TextInput } from "../../../../form/textInput";
import { Switch } from "../../../../form/switch";
import { updateNodeValue } from "../../../../../services/method";
import { webURL } from "../../../../../services/API";
import { Main, Styled } from "./style";
import { Select } from "../../../../form/select";

type TableRow = { name: string; type?: string };

type SqlTriggerProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

export function SqlTrigger({
  id,
  data,
  setChildMinimize,
  setChildSettings,
}: SqlTriggerProps) {
  const { setNodes } = useReactFlow();

  const [settings, setSettings] = React.useState<settingsDataType>({
    isToggle: false,
  });
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isFetchingTables, setIsFetchingTables] = React.useState(false);
  const [tablesError, setTablesError] = React.useState<string>("");

  const [tableOptions, setTableOptions] = React.useState<selectDataType[]>([]);

  React.useEffect(() => {
    if (setChildMinimize)
      setChildMinimize(() => {
        setIsExpanded((prev) => !prev);
      });
    if (setChildSettings)
      setChildSettings(() => {
        setSettings((prev) => ({ ...prev, isToggle: !prev.isToggle }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setChildMinimize, setChildSettings]);

  // Ensure sensible defaults so the backend poller always has values
  React.useEffect(() => {
    const updates: Record<string, unknown> = {};
    if (!data.value || data.value.limit === undefined) updates.limit = 1;
    if (!data.value || data.value.markProcessed === undefined)
      updates.markProcessed = true;
    if (!data.value || data.value.wantSpecificTable === undefined)
      updates.wantSpecificTable = false;
    if (!data.value || data.value.pollIntervalMs === undefined)
      updates.pollIntervalMs = 10000;
    if (Object.keys(updates).length) updateNodeValue(id, setNodes, updates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthenticate = (type: "add" | "remove", authData?: any) => {
    if (type === "add" && authData) {
      updateNodeValue(id, setNodes, { connection: authData });
    } else {
      updateNodeValue(id, setNodes, { connection: null });
    }
  };

  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    keyRef?: string
  ) => {
    if (!keyRef) return;
    const raw = e.target.value;
    const value =
      e.target.type === "number" ? (raw === "" ? "" : Number(raw)) : raw;
    updateNodeValue(id, setNodes, { [keyRef]: value });
  };

  const HIDDEN_TABLE = "table_events";

  React.useEffect(() => {
    const tables = Array.isArray(data.value?.tables) ? data.value.tables : [];
    const filteredTables = tables.filter(
      (table: TableRow) =>
        (table?.name || "").toLowerCase() !== HIDDEN_TABLE &&
        (table?.type || "").toLowerCase() !== HIDDEN_TABLE
    );
    const options = filteredTables.map((table: TableRow, index: number) => ({
      id: index + 1,
      name: table.name || `Table ${index + 1}`,
      refId: table.name || `table_${index + 1}`,
      isActive: (table.name || `table_${index + 1}`) === data.value?.table,
    }));
    setTableOptions(options);
  }, [data.value?.tables, data.value?.table]);

  const buildTablePayload = () => {
    const connection = data.value?.connection as any;
    const savedCreds =
      connection?.sql || connection?.usedCredentials || connection || {};

    const host = data.value?.host || savedCreds.host;
    const user = data.value?.user || savedCreds.user;
    const password = data.value?.password || savedCreds.password;
    const database = data.value?.database || savedCreds.database;
    const type =
      data.value?.dbType ||
      data.value?.type ||
      savedCreds?.sql?.type ||
      savedCreds?.type ||
      "sql";
    const port = data.value?.port || savedCreds?.port;
    return {
      host,
      user,
      password,
      database,
      type,
      port,
      ssl: savedCreds.ssl ?? data.value?.ssl ?? false,
      sslMode: savedCreds.sslMode ?? data.value?.sslMode ?? "preferred",
      caCert: savedCreds.caCert ?? data.value?.caCert,
      sslConfig:
        savedCreds.sslConfig ??
        data.value?.sslConfig ??
        savedCreds.ssl ??
        undefined,
      rejectUnauthorized:
        savedCreds.rejectUnauthorized ??
        data.value?.sslRejectUnauthorized ??
        false,
      useMariaDb: savedCreds.useMariaDb ?? data.value?.useMariaDb ?? false,
    };
  };

  const tablePayload = buildTablePayload();

  const handleSelectTable = async (selectId?: number) => {
    if (
      !tablePayload.host ||
      !tablePayload.user ||
      !tablePayload.password ||
      !tablePayload.database
    ) {
      setTablesError("Missing required credentials to fetch tables.");
      return;
    }

    setIsFetchingTables(true);
    setTablesError("");
    try {
      const response = await axios.post(`${webURL}/sql/tables`, tablePayload);
      const tables: TableRow[] = response?.data?.tables || [];
      const filteredTables = tables.filter(
        (table) => (table?.name || "").toLowerCase() !== HIDDEN_TABLE
      );
      const selectedTable =
        typeof selectId === "number"
          ? filteredTables[selectId - 1]?.name || filteredTables[selectId]?.name
          : undefined;

      updateNodeValue(id, setNodes, {
        tables: filteredTables,
        table:
          selectedTable || data.value?.table || filteredTables[0]?.name,
      });
    } catch (error: any) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Failed to fetch tables";
      setTablesError(message);
    } finally {
      setIsFetchingTables(false);
    }
  };
  React.useEffect(() => {
    setTableOptions((ops) => {
      const filtered = ops.filter(
        (op) => (op.refId || op.name || "").toLowerCase() !== HIDDEN_TABLE
      );
      return filtered.length === ops.length ? ops : filtered;
    });
  }, [data.value?.tables]);

  const handleSelectTableOption = (selectId: number) => {
    setTableOptions((ops) => {
      const filtered = ops.filter(
        (op) => (op.refId || op.name || "").toLowerCase() !== HIDDEN_TABLE
      );
      const updated = filtered.map((op) => ({
        ...op,
        isActive: op.id === selectId,
      }));
      const selected = updated.find((op) => op.id === selectId);
      if (selected?.refId && selected.refId.toLowerCase() !== HIDDEN_TABLE) {
        updateNodeValue(id, setNodes, { table: selected.refId });
      }
      return updated;
    });
  };
  const isWarning = !data.value?.connection;

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />

      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon">
              <IoWarningOutline size={21} />
            </span>
          </>
        ) : data.output ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon">
              <IoCheckmarkCircleOutline size={21} />
            </span>
          </>
        ) : null}

        {isExpanded ? (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ minWidth: "420px" }}>
            <Styled>
              <AuthenticateSelect
                type="sql"
                defaultId={data.value?.connection?.id}
                handleAuthenticate={handleAuthenticate}
                background={data.background}
              />

              {/* <div className="action" /> */}
              <div className="actions">
                <button
                  className="btn1 withBorder"
                  disabled={
                    isFetchingTables ||
                    !tablePayload.host ||
                    !tablePayload.user ||
                    !tablePayload.password ||
                    !tablePayload.database
                  }
                  onClick={() => handleSelectTable()}>
                  {isFetchingTables ? "Fetching..." : "Fetch Tables"} in{" "}
                  {tablePayload?.type || "sql"}
                </button>
                {tablesError ? (
                  <span className="status error">{tablesError}</span>
                ) : null}
              </div>
              {tableOptions.length ? (
                <div className="mb-2 ">
                  <Select
                    title="Table"
                    data={tableOptions}
                    handleSelectItem={handleSelectTableOption}
                    isSmall={true}
                  />
                </div>
              ) : (
                <TextInput
                  keyRef="table"
                  title="Table"
                  placeholder="users"
                  value={data.value?.table}
                  handleChange={handleChangeInput}
                />
              )}
              <TextInput
                keyRef="limit"
                value={
                  data.value?.limit === undefined
                    ? ""
                    : String(data.value.limit)
                }
                title="Batch Size"
                type="number"
                min={1}
                placeholder="1"
                handleChange={handleChangeInput}
              />
              <Switch
                className="one"
                title="Mark events processed"
                value={!!data.value?.markProcessed}
                setValue={() =>
                  updateNodeValue(id, setNodes, {
                    markProcessed: !data.value?.markProcessed,
                  })
                }
              />
              <Switch
                title="Want Specific Table"
                value={!!data.value?.wantSpecificTable}
                setValue={() =>
                  updateNodeValue(id, setNodes, {
                    wantSpecificTable: !data.value?.wantSpecificTable,
                  })
                }
              />
              <p className="note">
                Dragify will create a `table_events` outbox table and poll for
                new rows. Use your SQL connection above to control which
                database is watched.
              </p>
            </Styled>
          </ExpandedBlock>
        ) : null}
      </Main>
    </>
  );
}